import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useCurrency } from "../context/CurrencyContext";
import api, { timelineApi } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ArrowLeft, CheckCircle2, AlertCircle, Download, RefreshCw } from "lucide-react";
import { cn } from "../lib/utils";
import TaskTimeline from "../components/TaskTimeline";
import ProjectPassport from "../components/client-portal/ProjectPassport";
import ProjectPayments from "../components/client-portal/ProjectPayments";
import { downloadClientDocument, Hint, PenaltyPanel, penaltyRowsFromPayments } from "../components/client-portal/PaymentBits";
import { fmtDate, days } from "../components/client-portal/format";
import { tr } from "../components/client-portal/i18n";
import { isTaskDone, stageBadgeStyle } from "../lib/taskStage";

// A task in the client's words: its timeline stage, named as the manager set it
// up in /settings/timelines — unless the client owes us an answer.
const taskStatus = (t) =>
  t.needs_approval
    ? { label: tr("Чекає вашого погодження"), tone: "bg-orange-500/15 text-orange-500 border-orange-500/40" }
    : { label: t.resolved_stage?.name || "—", style: stageBadgeStyle(t.resolved_stage?.color) };

const VERSION_STATUS = {
  approved: tr("Погоджено"),
  revision_requested: tr("Запитано правки"),
  pending: tr("Очікує вашого перегляду"),
};

const Fact = ({ label, children }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">{label}</p>
    <p className="text-sm text-zinc-100 font-medium mt-0.5 truncate">{children}</p>
  </div>
);

const ClientProjectDetailPage = () => {
  const { projectId } = useParams();
  const { formatAmount } = useCurrency();
  const [portal, setPortal] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taskTimelines, setTaskTimelines] = useState({});
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskFeedback, setTaskFeedback] = useState("");
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    fetchProjectData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const res = await api.get("/client/portal");
      setPortal(res.data);
      const projectData = res.data.projects?.find((p) => p.id === projectId) || null;
      setProject(projectData);
      if (projectData) {
        const map = {};
        await Promise.all(
          (projectData.tasks || []).map(async (t) => {
            try {
              const tl = await timelineApi.getTaskTimeline(t.id);
              if (tl.data) map[t.id] = tl.data;
            } catch (_) { /* task may have no timeline */ }
          }),
        );
        setTaskTimelines(map);
      }
    } catch (error) {
      toast.error(tr("Не вдалося завантажити проєкт"));
    } finally {
      setLoading(false);
    }
  };

  const handleTaskReview = async (taskId, action) => {
    setReviewing(true);
    try {
      await api.post(`/client/tasks/${taskId}/review`, { action, feedback: taskFeedback });
      toast.success(action === "approve" ? tr("Роботу погоджено") : tr("Запит на правки надіслано"));
      setSelectedTask(null);
      setTaskFeedback("");
      fetchProjectData();
    } catch (error) {
      toast.error(error?.response?.status === 403 ? tr("Режим перегляду — дія недоступна") : tr("Не вдалося надіслати відгук"));
    } finally {
      setReviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-4 md:p-6">
        <Card className="bg-zinc-900/60 border-zinc-800 rounded-2xl">
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-zinc-50 font-['Manrope']">{tr("Проєкт не знайдено")}</h3>
            <Link to="/client/projects">
              <Button variant="outline" className="mt-4">
                <ArrowLeft className="h-4 w-4 mr-2" /> {tr("До моїх проєктів")}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tasks = project.tasks || [];
  const doneTasks = tasks.filter(isTaskDone).length;
  const payments = (portal?.payments || []).filter((p) => p.project_id === project.id);
  // Payment demands are shown in the Payments block (with the real penalty);
  // here only what we asked the client to answer.
  const requests = (portal?.action_required || []).filter(
    (r) => r.project_id === project.id && r.request_type !== "payment_request",
  );
  const selTl = selectedTask ? taskTimelines[selectedTask.id] : null;
  const selStage = selTl?.stages?.[selTl.current_stage_index || 0];

  return (
    <div className="p-4 md:p-6 space-y-5">
      <Link to="/client/projects" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-100">
        <ArrowLeft className="h-4 w-4" /> {tr("Мої проєкти")}
      </Link>

      <PenaltyPanel
        rows={penaltyRowsFromPayments(payments, { [project.id]: project.penalty_rule })}
        rule={project.penalty_rule?.enabled ? project.penalty_rule : null}
      />

      <ProjectPassport
        project={project}
        clientContacts={portal?.client_contacts || []}
        tasksDone={doneTasks}
        tasksTotal={tasks.length}
      />

      <ProjectPayments
        payments={payments}
        penaltyRule={project.penalty_rule}
        onDownload={(id, type) => downloadClientDocument(id, type, "uk")}
      />

      {requests.length > 0 && (
        <div className="space-y-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-semibold">{tr("Від вас")}</p>
            <h3 className="text-lg font-bold text-zinc-50 font-['Manrope'] mt-0.5">{tr("Запити від нашої команди")}</h3>
          </div>
          <Card className="bg-zinc-900/60 border-zinc-800 rounded-2xl">
            <CardContent className="p-2">
              {requests.map((req) => (
                <div key={req.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-zinc-800/40">
                  <span className={cn("w-2 h-2 rounded-full flex-shrink-0", req.is_overdue ? "bg-red-500" : "bg-orange-500")} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-zinc-100 font-medium truncate" title={req.title}>{req.title}</p>
                    <p className={cn("text-xs truncate", req.is_overdue ? "text-red-500" : "text-zinc-500")}>
                      {req.is_overdue
                        ? `${tr("Треба було відповісти до")} ${fmtDate(req.deadline) || "—"}`
                        : `${tr("Залишилось")} ${req.time_remaining_hours < 24 ? `${Math.max(1, Math.ceil(req.time_remaining_hours))} ${tr("год")}` : days(Math.ceil(req.time_remaining_hours / 24))}`}
                    </p>
                  </div>
                  <Link to="/client#client-requests">
                    <Button size="sm" className="h-8 bg-orange-500 hover:bg-orange-600 text-white rounded-lg">{tr("Відповісти")}</Button>
                  </Link>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-semibold">{tr("Задачі")}</p>
          <h3 className="text-lg font-bold text-zinc-50 font-['Manrope'] mt-0.5 flex items-center gap-2">
            {tr("Що ми робимо")} <span className="text-zinc-500 font-normal">· {doneTasks} {tr("з")} {tasks.length} {tr("готово")}</span>
          </h3>
        </div>
        <Card className="bg-zinc-900/60 border-zinc-800 rounded-2xl">
          <CardContent className="p-2">
            {tasks.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-8">{tr("Задачі зʼявляться тут, щойно почнеться робота.")}</p>
            ) : (
              tasks.map((task) => {
                const st = taskStatus(task);
                const tl = taskTimelines[task.id];
                return (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => setSelectedTask(task)}
                    className="w-full grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1.6fr)_170px_150px_110px_110px] items-center gap-x-4 gap-y-1 px-3 py-2.5 rounded-lg text-left hover:bg-zinc-800/40"
                  >
                    <p className="text-sm text-zinc-100 font-medium truncate" title={task.name}>{task.name}</p>
                    <span className={cn("justify-self-end md:justify-self-start inline-flex px-2 py-0.5 rounded-md border text-[11px] whitespace-nowrap", st.tone)} style={st.style}>
                      {st.label}
                    </span>
                    <div className="hidden md:block">
                      {tl?.stages?.length > 0 ? (
                        <TaskTimeline stages={tl.stages} currentStageIndex={tl.current_stage_index || 0} size="sm" showLabels={false} />
                      ) : <span className="text-xs text-zinc-600">—</span>}
                    </div>
                    <p className="hidden md:block text-xs text-zinc-400 tabular-nums">{fmtDate(task.deadline) || "—"}</p>
                    <p className="hidden md:block text-xs text-zinc-100 tabular-nums text-right">
                      {task.price > 0 ? formatAmount(task.price, task.currency || "USD") : "—"}
                    </p>
                  </button>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>

      {/* Task card — what a client needs: status, deadline, stage, price,
          revisions left, the results to look at; approve only when due. */}
      <Dialog open={!!selectedTask} onOpenChange={() => { setSelectedTask(null); setTaskFeedback(""); }}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedTask && (
            <>
              <DialogHeader>
                <DialogTitle className="text-zinc-50 font-['Manrope'] pr-6">{selectedTask.name}</DialogTitle>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge variant="outline" className={cn("text-xs", taskStatus(selectedTask).tone)}>
                    {taskStatus(selectedTask).label}
                  </Badge>
                  {selectedTask.task_type && <span className="text-xs text-zinc-500">{selectedTask.task_type}</span>}
                </div>
              </DialogHeader>

              <div className="space-y-4 py-1">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-xl border border-zinc-800 p-3">
                  <Fact label={tr("Строк")}>{fmtDate(selectedTask.deadline) || tr("Уточнюється")}</Fact>
                  <Fact label={tr("Етап")}>{selStage?.name || taskStatus(selectedTask).label}</Fact>
                  <Fact label={tr("Вартість")}>
                    {selectedTask.price > 0 ? formatAmount(selectedTask.price, selectedTask.currency || "USD") : "—"}
                  </Fact>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold flex items-center gap-1">
                      {tr("Правки")}
                      <Hint text={tr("Скільки кіл правок входить у вартість. Понад ліміт — за окремою домовленістю.")} />
                    </p>
                    <p className="text-sm text-zinc-100 font-medium mt-0.5">
                      {Math.max(0, (selectedTask.free_revisions_limit ?? 3) - Math.max(0, (selectedTask.client_versions_count || 0) - 1))} {tr("з")} {selectedTask.free_revisions_limit ?? 3} {tr("безкоштовних")}
                    </p>
                  </div>
                </div>

                {selTl?.stages?.length > 0 && (
                  <div className="rounded-xl border border-zinc-800 p-3">
                    <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold mb-2">{tr("Хід робіт")}</p>
                    <TaskTimeline stages={selTl.stages} currentStageIndex={selTl.current_stage_index || 0} size="sm" showLabels />
                  </div>
                )}

                {(selectedTask.brief || selectedTask.description) && (
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold mb-1">{tr("Що робимо")}</p>
                    <p className="text-sm text-zinc-300 whitespace-pre-line">{selectedTask.brief || selectedTask.description}</p>
                  </div>
                )}

                <div>
                  <p className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-semibold mb-2">{tr("Результати")}</p>
                  {selectedTask.versions?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedTask.versions.map((version, idx) => (
                        <div key={version.id || idx} className="rounded-lg border border-zinc-800 p-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-zinc-100">
                              {tr("Версія")} {version.version_number || idx + 1}
                              {version.uploaded_at && <span className="text-zinc-500 font-normal"> · {fmtDate(version.uploaded_at)}</span>}
                            </span>
                            {version.status && (
                              <span className="text-xs text-zinc-400">{VERSION_STATUS[version.status] || version.status}</span>
                            )}
                          </div>
                          {version.files?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {version.files.map((file, fi) => (
                                <a key={fi} href={file.url || file} target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-zinc-800/50 hover:bg-zinc-800 text-sm text-zinc-300">
                                  <Download className="h-4 w-4 text-zinc-400" />
                                  <span className="truncate">{file.name || file.filename || `${tr("Файл")} ${fi + 1}`}</span>
                                </a>
                              ))}
                            </div>
                          )}
                          {version.client_feedback && (
                            <p className="mt-2 text-xs text-zinc-400"><span className="text-zinc-500">{tr("Ваш відгук:")}</span> {version.client_feedback}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-500">{tr("Поки немає — покажемо результат тут, щойно він буде готовий.")}</p>
                  )}
                </div>

                {selectedTask.needs_approval && (
                  <div className="space-y-3 pt-3 border-t border-zinc-800">
                    <p className="text-sm text-zinc-300">{tr("Роботу готово — перегляньте результат і погодьте або опишіть правки.")}</p>
                    <Textarea
                      value={taskFeedback}
                      onChange={(e) => setTaskFeedback(e.target.value)}
                      placeholder={tr("Що змінити (необов’язково при погодженні)")}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[80px]"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button onClick={() => handleTaskReview(selectedTask.id, "revision")} disabled={reviewing}
                        variant="outline" className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10">
                        {tr("Потрібні правки")}
                      </Button>
                      <Button onClick={() => handleTaskReview(selectedTask.id, "approve")} disabled={reviewing}
                        className="bg-green-600 hover:bg-green-700 text-white">
                        {reviewing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                        {tr("Погодити роботу")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientProjectDetailPage;
